import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

import FootnoteLink from '$partials/glossary/footnote-link.jsx';
import GlossaryLink from '$partials/glossary/glossary-link.jsx';
import GlossaryModal from '$partials/glossary/glossary-modal.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const RichTextWithGlossary = (props) => {
    const [selectedTerm, setSelectedTerm] = useState(null);
    const containerRef = useRef();

    useEffect(() => {
        // Override glossary link click events
        if (containerRef.current) {
            const glossaryLinks = containerRef.current.getElementsByClassName('glossary-term');

            for (let link of glossaryLinks) {
                link.onclick = (e) => {
                    e.preventDefault();

                    setSelectedTerm(link.dataset.glossary);
                };
            }
        }
    }, []);

    if (!props?.html) { return null; }

    try {
        const rx1 = /<a class="glossary-term" data-glossary="([^"]+)" data-index="(\d+)" href="([^"]+)[^>]+>([^<]+)<\/a>/gi;
        const rx2 = /<a class="footnote" data-footnote-label="([^"]+)" href="([^"]+)[^>]+>([^<]+)<\/a>/gi;

        let output;

        if (rx1.test(props.html)) {
            output = props.html.replaceAll(rx1, (match, termId, index, href, label) => {
                return ReactDOMServer.renderToString(React.createElement(GlossaryLink, {
                    href,
                    index,
                    label,
                    termId
                }));
            });
        }

        if (rx2.test(rx1.test(props.html) ? output : props.html)) {
            output = (rx1.test(props.html) ? output : props.html).replaceAll(rx2, (match, footnoteLabel, href, body) => {
                return ReactDOMServer.renderToString(React.createElement(FootnoteLink, {
                    footnoteLabel,
                    href,
                    body
                }));
            });
        }

        if ((!rx1.test(props.html)) && (!rx2.test(props.html))) {
            output = props.html;
        }

        const { html, ...rest } = props;

        return (
            <>
                <div
                    { ...rest }
                    className={props.className || 'rich-text'}
                    dangerouslySetInnerHTML={{ __html: output }}
                    ref={containerRef} />

                {(selectedTerm) &&
                    <GlossaryModal
                        isOpen={true}
                        onClose={() => setSelectedTerm(null)}
                        termId={selectedTerm} />
                }
            </>
        );
    } catch (err) {
        console.error('Error occurred:', err);

        const { html, ...rest } = props;

        return (
            <div
                { ...rest }
                className={props.className || 'rich-text'}
                dangerouslySetInnerHTML={{ __html: props.html }} />
        );
    }

};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    className: PropTypes.string,
    html: PropTypes.string.isRequired,
};

RichTextWithGlossary.propTypes = propTypes;

export default RichTextWithGlossary;
